import { useCustomToast } from '@elkaso-app/web-design';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';
import type { FamilySuppliers } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

type TData = FamilySuppliers;
type TError = AxiosError;
type TQueryKey = [string, number | undefined];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

// GET ums/v1/cms/master-product-families/:familyId/suppliers
// Suppliers currently carrying variants under this family — used to warn
// about supplier impact before merging two families.
export function useGetFamilySuppliersApi(familyId?: number, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const toast = useCustomToast();
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${masterProductFamiliesEndpoint}/${familyId}/suppliers`);
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([`${masterProductFamiliesEndpoint}/suppliers`, familyId], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn && familyId != null,
    retry: false,
    ...restOptions,
  });
}
